// Import from shared-utils library (best practice - proper encapsulation)
import { RouteStateService } from '@fitness-force/shared-utils';
import { KeyValuePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, ChangeDetectorRef, input, output, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { PlanmasterService, SubscriptionSignupByUuidService,AdvancedSettingService} from '@fitness-force/api';
import { PlanCategory, Status } from '@fitness-force/enum';
import type { PlanAddons, ServiceTypeResponse } from '@fitness-force/models';
import { Store } from '@ngrx/store';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { DrawerModule } from 'primeng/drawer';
import { StyleClassModule } from 'primeng/styleclass';
import { BehaviorSubject } from 'rxjs';
import { CurrencyformatPipe } from '@fitness-force/pipes/currency';
import { SERVICETYPE_LIST_STATE } from '@fitness-force/state';
import { CheckboxModule } from 'primeng/checkbox';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SkeletonModule } from 'primeng/skeleton';
import { ButtonModule } from 'primeng/button';
import { RadioButtonModule } from 'primeng/radiobutton';
import { DatePickerModule } from 'primeng/datepicker';

import { ToastMessageService } from '@fitness-force/api';
import { AddonFilterPipe } from '../../../../pipes/common-pipes/src/lib/common.filter.pipe';


@Component({
  selector: 'billing-add-on',
  templateUrl: './billing-add-on.component.html',
  styleUrls: ['./billing-add-on.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers:[ToastMessageService],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DrawerModule,
    SelectModule,
    InputTextModule,
    StyleClassModule,
    CheckboxModule,
    CurrencyformatPipe,
    AddonFilterPipe,
    SkeletonModule,
    ButtonModule,
    RadioButtonModule,
    DatePickerModule,
    TranslateModule, KeyValuePipe, NgClass
],
})
export class BillingAddOnComponent implements OnInit {
  private store = inject(Store);
  private cd = inject(ChangeDetectorRef);
  private planService = inject(PlanmasterService);
  private stateService = inject(RouteStateService);
  private openApiService = inject(SubscriptionSignupByUuidService);
  private fb = inject(UntypedFormBuilder);
  private advanceSettingService = inject(AdvancedSettingService);
  toastMessageService = inject(ToastMessageService);
  private translateService = inject(TranslateService);

  readonly showAddonsSidebar = input<boolean>(false, { alias: "visible" });
  readonly planId = input<number>(0);
  readonly uuid = input<string>("");
  readonly tenantId = input<number>(0);
  readonly selectedAddons = input(new BehaviorSubject<PlanAddons[]>([]));
  readonly mode = input<string>("billing");
  readonly closeAddonSidebar = output<{
    callbreakup: boolean;
    selectedAddons: PlanAddons[];
}>();

  addonCategories: any;
  planAddons$ = new BehaviorSubject<PlanAddons[]>([]);
  filteredPlanAddons$ = new BehaviorSubject<PlanAddons[]>([]);
  serviceTypeCollection!: any[];
  recurringPlanServices$ = new BehaviorSubject<PlanAddons[]>([]);
  nonRecurringPlanServices$ = new BehaviorSubject<PlanAddons[]>([]);
  recurringGroupedData = new Map<number, PlanAddons[]>();
  nonRecurringGroupedData = new Map<number, PlanAddons[]>();
  isRecurringSubscriptions:boolean=false;
  isNonRecurringSubscriptions:boolean=false;
  planAddonsForm: UntypedFormGroup = {} as UntypedFormGroup;
  callbreakup:boolean=true;
  showSkeleton:boolean=true;
  isInclusive:boolean=false;

  constructor() {

      this.addonCategories = [
        {name: 'All', value:''},
        {name: 'Recurring', value:'recurring'},
        {name: 'Non-recurring',value:'nonrecurring'},
        ];

      this.planAddonsForm = this.fb.group({
        selected_category: new UntypedFormControl('', []),
        selected_serviuce_category: new UntypedFormControl('', []),
      });
    }

  ngOnInit(): void {
    this.getSettings();
    this.getServiceCategories();
    this.getAllData();
  }

  getSettings() {
    let service:any;
    service = this.advanceSettingService.getAdvanceSettings(this.tenantId());
    if (service) {
      service.subscribe({
        next: (res: any) => {
          if (res.items != null) {
            let tax_calculation: any = res.items.find((x: { key: string; }) => x.key.toLowerCase() == "tax_calculation")
            if (tax_calculation != null) {
              if (tax_calculation.value.toLowerCase() == 'inclusive')
                this.isInclusive = true;
            }
          }
        }
      });
    }
  }

  getServiceCategories()
  {
    this.store.select(SERVICETYPE_LIST_STATE).subscribe(
      {
        next: (res: any) => {
          if (res && res.length > 0) {
            this.serviceTypeCollection = [];
            this.serviceTypeCollection.push({
              name: 'All',
              value: "",
            });
            res.forEach((serviceType: ServiceTypeResponse) => {
              let status = serviceType.status.toString();
              let varStatus: string = Status[Status.Active];
              if (status === varStatus) {
                this.serviceTypeCollection.push({
                  name: serviceType.name_primary_language,
                  value: serviceType.id,
                });
              }
            });
          }
          else {
            this.serviceTypeCollection = [];
          }
        }, error: () => {
          this.serviceTypeCollection = [];
        }
      })
  }

  getAllData() {
    if(this.stateService.getAddonList().length > 0 && this.stateService.getTenantId() == this.tenantId() && this.stateService.getPlanId() == this.planId())
    {
      this.planAddons$.next(this.stateService.getAddonList());
      this.filteredPlanAddons$.next(this.stateService.getAddonList());
      this.filterServiceCategories(this.planAddons$.value);
      this.restoreSelectedAddonsFromLoadedList();
    }
    else{
        let service:any;
        service = this.planService.getSubscriptionAddonsByPlanId(this.tenantId(), this.planId())
        service.subscribe(
           {
             next: (res: any) => {
               this.planAddons$.next((res.items ? res.items : []));
               this.filteredPlanAddons$.next((res.items ? res.items : []));
               this.stateService.setAddonList(this.planAddons$.value);
               this.stateService.setTenantTd(this.tenantId());
               this.stateService.setPlanTd(this.planId());
               this.filterServiceCategories(this.planAddons$.value);
               this.restoreSelectedAddonsFromLoadedList();
               this.cd.detectChanges();
             }, error: (error: any) => {
               this.planAddons$.next(([]));
               this.filteredPlanAddons$.next(([]));
               this.cd.detectChanges();
             }
         });
    }
  }

  FilterByServiceCategory()
  {
    if(this.SelectedServiceCategoryFormControl.value.value =="") this.filterServiceCategories(this.planAddons$.value);
    else 
    {
      const addons = this.planAddons$.value.filter(o => o.category_id == this.SelectedServiceCategoryFormControl.value.value);
      this.filterServiceCategories(addons);
    }
     
  }

  FilterByCategory()
  {
     if(this.SelectedCategoryFormControl.value.value == 'recurring')
     {
      const recurring = this.planAddons$.value.filter(o => this.isRecurringCategory(o.subscription_category));
      this.filterServiceCategories(recurring);
     }
     else if(this.SelectedCategoryFormControl.value.value == 'nonrecurring'){
      const nonrecurring = this.planAddons$.value.filter(o => this.isNonRecurringCategory(o.subscription_category));
      this.filterServiceCategories(nonrecurring);
     }
     else{
      this.filterServiceCategories(this.planAddons$.value);
     }
  }


  filterServiceCategories(planAddOns:PlanAddons[])
  {
    this.isRecurringSubscriptions = false;
    this.isNonRecurringSubscriptions = false;

    this.recurringPlanServices$.next(planAddOns.filter(o => this.isRecurringCategory(o.subscription_category)));

     const nonrecurring = planAddOns.filter(o => this.isNonRecurringCategory(o.subscription_category));
     this.nonRecurringPlanServices$.next(nonrecurring);

     if(this.recurringPlanServices$.value.length > 0) this.isRecurringSubscriptions = true;
     if(this.nonRecurringPlanServices$.value.length > 0) this.isNonRecurringSubscriptions = true;
     this.mappingAddons();
  }

  mappingAddons()
  {
    this.nonRecurringGroupedData.clear();
    this.recurringGroupedData.clear();

    this.nonRecurringPlanServices$.value.forEach((item) => {
      const key = item.category_id;
      if (!this.nonRecurringGroupedData.has(key)) {
        this.nonRecurringGroupedData.set(key, []);
      }
      const groupedAddons = this.nonRecurringGroupedData.get(key)!;
      if (!groupedAddons.some((addon) => addon.id === item.id)) {
        groupedAddons.push(item);
      }

      if (!this.planAddonsForm.contains('selectedAddon_' + item.id)) {
        const selectedAddons = this.selectedAddons();
        const selectedAddon = selectedAddons.value.find(o => o.id === item.id);
        if (selectedAddon) {
          this.planAddonsForm.addControl('selectedAddon_' + item.id, new UntypedFormControl(true));
          if (this.mode() === 'midcycleaddon') {
            this.planAddonsForm.addControl('selectedAddonStartdate_' + item.id, new UntypedFormControl(selectedAddon.start_date));
          }
        } else {
          this.planAddonsForm.addControl('selectedAddon_' + item.id, new UntypedFormControl(false));
          if (this.mode() === 'midcycleaddon') {
            this.planAddonsForm.addControl('selectedAddonStartdate_' + item.id, new UntypedFormControl());
          }
        }
      } else {
        const selectedAddon = this.selectedAddons().value.find(o => o.id === item.id);
        this.planAddonsForm.get('selectedAddon_' + item.id)?.setValue(!!selectedAddon);
        if (this.mode() === 'midcycleaddon') {
          if (!this.planAddonsForm.contains('selectedAddonStartdate_' + item.id)) {
            this.planAddonsForm.addControl(
              'selectedAddonStartdate_' + item.id,
              new UntypedFormControl(selectedAddon?.start_date ?? null)
            );
          } else if (selectedAddon) {
            this.planAddonsForm.get('selectedAddonStartdate_' + item.id)?.setValue(selectedAddon.start_date ?? null);
          }
        }
      }
    });

    this.recurringPlanServices$.value.forEach((item) => {
      const key = item.category_id;
      if (!this.recurringGroupedData.has(key)) {
        this.recurringGroupedData.set(key, []);
      }
      const groupedAddons = this.recurringGroupedData.get(key)!;
      if (!groupedAddons.some((addon) => addon.id === item.id)) {
        groupedAddons.push(item);
      }

      if (!this.planAddonsForm.contains('selectedAddon_' + item.id)) {
        const selectedAddons = this.selectedAddons();
        const selectedAddon = selectedAddons.value.find(o => o.id === item.id);
        if (selectedAddon) {
          this.planAddonsForm.addControl('selectedAddon_' + item.id, new UntypedFormControl(true));
          if (this.mode() === 'midcycleaddon') {
            this.planAddonsForm.addControl('selectedAddonStartFrom_' + item.id, new UntypedFormControl(selectedAddon.start_mode));
          }
        } else {
          this.planAddonsForm.addControl('selectedAddon_' + item.id, new UntypedFormControl(false));
          if (this.mode() === 'midcycleaddon') {
            this.planAddonsForm.addControl('selectedAddonStartFrom_' + item.id, new UntypedFormControl());
          }
        }
      } else {
        const selectedAddon = this.selectedAddons().value.find(o => o.id === item.id);
        this.planAddonsForm.get('selectedAddon_' + item.id)?.setValue(!!selectedAddon);
        if (this.mode() === 'midcycleaddon') {
          if (!this.planAddonsForm.contains('selectedAddonStartFrom_' + item.id)) {
            this.planAddonsForm.addControl(
              'selectedAddonStartFrom_' + item.id,
              new UntypedFormControl(selectedAddon?.start_mode ?? null)
            );
          } else if (selectedAddon) {
            this.planAddonsForm.get('selectedAddonStartFrom_' + item.id)?.setValue(selectedAddon.start_mode ?? null);
          }
        }
      }
    });
    this.planAddonsForm.updateValueAndValidity();
    this.showSkeleton=false;
    this.cd.detectChanges();
  }

  getServiceCategoryName(categoryId:number)
  {
    return this.planAddons$.value.find(o => o.category_id ==categoryId)!;
  }

  setRequired(event:any, addonId:number)
  {
    if(this.mode() == 'midcycleaddon')
    {
      if(event.checked)
      {
        if(this.getFormControl('selectedAddonStartdate_' + addonId))
        {
          this.getFormControl('selectedAddonStartdate_' + addonId).addValidators([Validators.required]);
          this.getFormControl('selectedAddonStartdate_' + addonId).updateValueAndValidity();
        }
        if(this.getFormControl('selectedAddonStartFrom_' + addonId))
        {
          this.getFormControl('selectedAddonStartFrom_' + addonId).addValidators([Validators.required]);
          this.getFormControl('selectedAddonStartFrom_' + addonId).updateValueAndValidity();
        }
      }
      else{
        if(this.getFormControl('selectedAddonStartdate_' + addonId)) 
        {
          this.getFormControl('selectedAddonStartdate_' + addonId)!.clearValidators();
          this.getFormControl('selectedAddonStartdate_' + addonId)!.setValue(null);
          this.getFormControl('selectedAddonStartdate_' + addonId).updateValueAndValidity();
        } 
        if(this.planAddonsForm.get('selectedAddonStartFrom_' + addonId))
        {
          this.getFormControl('selectedAddonStartFrom_' + addonId).clearValidators();
          this.getFormControl('selectedAddonStartFrom_' + addonId).setValue(null);
          this.getFormControl('selectedAddonStartFrom_' + addonId).updateValueAndValidity();
        } 
      }
      this.planAddonsForm.updateValueAndValidity();
      this.cd.detectChanges();
    }
  }


  private restoreSelectedAddonsFromLoadedList(): void {
    const selectedAddons = this.selectedAddons();
    const existingSelection = selectedAddons.value;
    if (!existingSelection.length) {
      return;
    }

    const restoredAddons = this.planAddons$.value
      .filter((addon) => existingSelection.some((selected) => selected.id === addon.id))
      .map((addon) => {
        const existing = existingSelection.find((selected) => selected.id === addon.id);
        return {
          ...addon,
          start_mode: existing?.start_mode ?? addon.start_mode ?? null,
          start_date: existing?.start_date ?? addon.start_date ?? null,
          is_recurring: existing?.is_recurring ?? this.isRecurring(addon),
        };
      });

    if (restoredAddons.length > 0) {
      selectedAddons.next(restoredAddons);
      this.mappingAddons();
    }
  }

  closeSidebar(withoutSelection:boolean)
  {
   if (withoutSelection) {
    this.closeAddonSidebar.emit({
      callbreakup: false,
      selectedAddons: this.selectedAddons().value,
    });
    return;
   }

   this.planAddonsForm.markAllAsTouched();
   if(this.planAddonsForm.valid)
   {
    const formValues = this.planAddonsForm.getRawValue();
    let valuesArray = Object.keys(formValues);
    const selectedServices:PlanAddons[]=[];
    valuesArray.forEach(o => {
      if(formValues[o] == true)
      {
        const addonId = parseInt(o.split('_')[1]);
        let addOn = this.planAddons$.value.find(a => a.id == parseInt(o.split('_')[1]))!;
        addOn.is_recurring = this.isRecurring(addOn);
        if(formValues['selectedAddonStartFrom_'+addonId] && formValues['selectedAddonStartFrom_'+addonId] == "" && formValues['selectedAddonStartdate_'+addonId] && formValues['selectedAddonStartdate_'+addonId] == "")
        {
          addOn.start_date = null;
          addOn.start_mode = null;
          selectedServices.push(addOn);
        }
        else{
          if(formValues['selectedAddonStartFrom_'+addonId] && formValues['selectedAddonStartFrom_'+addonId] !== "")
          {
            addOn.start_mode = formValues['selectedAddonStartFrom_'+addonId];
            addOn.start_date=null;
          }
          else if(formValues['selectedAddonStartdate_'+addonId] && formValues['selectedAddonStartdate_'+addonId] !== "")
          {
            addOn.start_date = formValues['selectedAddonStartdate_'+addonId];
            addOn.start_mode = null;
          }
          else{
            addOn.start_date = null;
            addOn.start_mode = null;
          }
          selectedServices.push(addOn);
        }
      }
    });

    if(selectedServices.length == 0 && !withoutSelection)
    {
         this.translateService.get('BILLING_ADDONS.ADD_ON_REQUIRED_MSG')
              .subscribe((translation) => {
                this.toastMessageService.showWarning(translation);
         });
        return;
    }
    const selectedAddons = this.selectedAddons();
    if(this.mode() =='midcycleaddon')
    {
      this.callbreakup= true;
    }
    else{
      if(this.arraysAreNotSame(selectedAddons.value, selectedServices)) this.callbreakup= true;
      else this.callbreakup= false;
    }
    selectedAddons.next(selectedServices);
    this.closeAddonSidebar.emit({callbreakup:this.callbreakup,selectedAddons:selectedServices})
   }
   else{
    Object.keys(this.planAddonsForm.controls).forEach((field) => {
      const control = this.planAddonsForm.get(field);
      control?.markAsTouched({ onlySelf: true });
    });
   }
   this.callbreakup= false;
  }

  isRecurring(addOn:PlanAddons)
  {
    return this.isRecurringCategory(addOn.subscription_category);
  }

  private isRecurringCategory(subscriptionCategory: PlanCategory | string | number): boolean {
    return this.matchesPlanCategory(subscriptionCategory, PlanCategory.Contract)
      || this.matchesPlanCategory(subscriptionCategory, PlanCategory.PayAsYouGo);
  }

  private isNonRecurringCategory(subscriptionCategory: PlanCategory | string | number): boolean {
    return this.matchesPlanCategory(subscriptionCategory, PlanCategory.DropIn)
      || this.matchesPlanCategory(subscriptionCategory, PlanCategory.PaidInFull);
  }

  private matchesPlanCategory(
    subscriptionCategory: PlanCategory | string | number,
    category: PlanCategory
  ): boolean {
    return subscriptionCategory == category
      || subscriptionCategory?.toString() === PlanCategory[category]
      || Number(subscriptionCategory) === category;
  }

  arraysAreNotSame(arr1:any, arr2:any) {
    if (arr1.length !== arr2.length) {
      return true; // Different lengths, so they are not the same
    }
  
    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) {
        return true; // Found a different element, so they are not the same
      }
    }
    return false; // Arrays are the same
  }

  get SelectedCategoryFormControl():UntypedFormControl
  {
    return this.planAddonsForm.controls["selected_category"] as UntypedFormControl;
  }


  get SelectedServiceCategoryFormControl():UntypedFormControl
  {
    return this.planAddonsForm.controls["selected_serviuce_category"] as UntypedFormControl;
  }

  getFormControl(formControlName:string):UntypedFormControl
  {
    return this.planAddonsForm.get(formControlName) as UntypedFormControl;
  }

}